import React, { useMemo, useContext, useReducer, useCallback, isValidElement, cloneElement } from 'react';

const TranslationsContext = /*#__PURE__*/React.createContext(undefined);

// Not extending TranslationsContextValue so we can tailor the docs for each prop to be better
// suited to the provider, rather than for the useLanguage hook

/**
 * Provides a translation context for your application
 *
 * @example
 * import { VocabProvider } from '@vocab/react';
 *
 * <VocabProvider language="en">
 *   <App />
 * <VocabProvider />
 */
const VocabProvider = ({
  children,
  language,
  locale
}) => {
  const value = useMemo(() => ({
    language,
    locale
  }), [language, locale]);
  return /*#__PURE__*/React.createElement(TranslationsContext.Provider, {
    value: value
  }, children);
};

/**
 * @returns The `language` and `locale` values passed in to your `VocabProvider`
 */
const useLanguage = () => {
  const context = useContext(TranslationsContext);
  if (!context) {
    throw new Error('Attempted to access translation without Vocab context set. Did you forget to render VocabProvider?');
  }
  if (!context.language) {
    throw new Error('Attempted to access translation without language set. Did you forget to pass language to VocabProvider?');
  }
  return context;
};
const SERVER_RENDERING = typeof window === 'undefined';
function useTranslations(translations) {
  const {
    language,
    locale
  } = useLanguage();
  const [, forceRender] = useReducer(s => s + 1, 0);
  const translationsObject = translations.getLoadedMessages(language, locale || language);
  let ready = true;
  if (!translationsObject) {
    if (SERVER_RENDERING) {
      throw new Error(`Translations not synchronously available on server render. Applying translations dynamically server-side is not supported.`);
    }
    translations.load(language).then(() => {
      forceRender();
    });
    ready = false;
  }
  const t = useCallback((key, params) => {
    if (!translationsObject) {
      return ' ';
    }
    const message = translationsObject === null || translationsObject === void 0 ? void 0 : translationsObject[key];
    if (!message) {
      // eslint-disable-next-line no-console
      console.error(`Unable to find translation for key "${key}". Possible keys are ${Object.keys(translationsObject).map(v => `"${v}"`).join(', ')}`);
      return '';
    }
    const result = message.format(params);
    if (Array.isArray(result)) {
      for (let i = 0; i < result.length; i++) {
        const item = result[i];
        if (typeof item === 'object' && item && !item.key && /*#__PURE__*/isValidElement(item)) {
          result[i] = /*#__PURE__*/cloneElement(item, {
            key: `_vocab-${i}`
          });
        }
      }
    }
    return result;
  }, [translationsObject]);
  return {
    ready,
    t
  };
}

export { VocabProvider, useLanguage, useTranslations };
